/**
 * Maps backend error codes to user-friendly messages.
 * Backend errors now always include a "code" field via the global exception handler.
 */

export const ERROR_MESSAGES = {
  // Auth
  UNAUTHORIZED: 'Your session has expired. Please log in again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  CONFLICT: 'An account with this email already exists.',
  RATE_LIMITED: 'Too many attempts. Please wait a few minutes and try again.',

  // Validation
  VALIDATION_ERROR: 'Please check your input and try again.',

  // Data
  NOT_FOUND: 'The requested resource was not found.',
  DATABASE_ERROR: 'A server error occurred. Please try again.',

  // Generic
  INTERNAL_ERROR: 'Something went wrong. Please try again later.',
  NETWORK_ERROR: 'Unable to connect to the server. Please check your internet connection.',

  // Evaluation-specific
  EVALUATION_FAILED: 'The artwork evaluation could not be completed. Please try again.',
};

/**
 * Extract a user-friendly error message from a backend error response.
 *
 * @param {Error|Object} error - The error from fetch or a caught Error
 * @param {string} [fallback] - Fallback message if no match found
 * @returns {{ message: string, code: string|null }}
 */
export function parseError(error, fallback = 'Something went wrong. Please try again.') {
  if (!error) return { message: fallback, code: null };

  // Error object with a .code property (set by service layer)
  if (error.code && ERROR_MESSAGES[error.code]) {
    return { message: ERROR_MESSAGES[error.code], code: error.code };
  }

  // Standard Error with message
  if (error instanceof Error) {
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      return { message: ERROR_MESSAGES.NETWORK_ERROR, code: 'NETWORK_ERROR' };
    }
    return { message: error.message || fallback, code: error.code || null };
  }

  // Plain object with code
  if (typeof error === 'object' && error.code) {
    return { message: ERROR_MESSAGES[error.code] || error.detail || fallback, code: error.code };
  }

  // String
  if (typeof error === 'string') {
    return { message: error, code: null };
  }

  return { message: fallback, code: null };
}
